import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-kml";
import { InfoOutlined, Sensors } from "@mui/icons-material";
import {
  CheckCircle,
  Error,
  ErrorOutline,
  MenuBook,
  Warning,
} from "@mui/icons-material";
import { renderToString } from "react-dom/server";
import { getSensorIcon } from "../muiTabs/utils";
import { grey, red } from "@mui/material/colors";
import {
  isPointInPolygon as isGeoPointInPolygon,
  normalizePolygonCoordinates,
} from "../../utils/geoUtils";
import { isWeatherStation } from "../../config/sensors.config";

export const isPointInPolygon = isGeoPointInPolygon;

export const tileLayers = {
  googleSat: {
    name: "Google Satellite",
    url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    maxZoom: 21,
    attribution: "Imagery ©2025 Google",
  },
  osm: {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
};

export const MAIN_POLYGON_PATH_OPTIONS = {
  color: "white",
  weight: 3,
  opacity: 1,
  fillOpacity: 0.13,
};

export const FitPolygonBounds = ({ polygonCoordinates, maxZoom = 18 }) => {
  const map = useMap();
  const normalizedPolygon = useMemo(
    () => normalizePolygonCoordinates(polygonCoordinates),
    [polygonCoordinates],
  );

  useEffect(() => {
    if (normalizedPolygon.length >= 3) {
      try {
        const bounds = L.latLngBounds(normalizedPolygon);
        map.fitBounds(bounds, {
          maxZoom: maxZoom,
        });
      } catch {}
    }
  }, [map, normalizedPolygon, maxZoom]);
  return null;
};

export const normalizeMapPosition = (position) => {
  if (Array.isArray(position) && position.length >= 2) {
    const lat = Number(position[0]);
    const lng = Number(position[1]);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  const lat = Number(position?.lat ?? position?.latitude);
  const lng = Number(position?.lng ?? position?.longitude);

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return [lat, lng];
  }

  return null;
};

export const FitMapPosition = ({ position, zoom = 16 }) => {
  const map = useMap();
  const normalizedPosition = normalizeMapPosition(position);
  const lat = normalizedPosition?.[0];
  const lng = normalizedPosition?.[1];

  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    map.setView([lat, lng], zoom);
  }, [map, lat, lng, zoom]);

  return null;
};

export const DrawControl = ({
  onPolygonCreated,
  onPolygonEdited,
  onPolygonDraftChange,
  onPolygonDeleted,
  polygonCoordinates,
  allowPolygonDelete = true,
  mode = "main",
}) => {
  const map = useMap();
  const normalizedPolygon = useMemo(
    () => normalizePolygonCoordinates(polygonCoordinates),
    [polygonCoordinates],
  );
  const callbacksRef = useRef({
    onPolygonCreated,
    onPolygonEdited,
    onPolygonDraftChange,
    onPolygonDeleted,
  });

  useEffect(() => {
    callbacksRef.current = {
      onPolygonCreated,
      onPolygonEdited,
      onPolygonDraftChange,
      onPolygonDeleted,
    };
  }, [
    onPolygonCreated,
    onPolygonEdited,
    onPolygonDraftChange,
    onPolygonDeleted,
  ]);

  useEffect(() => {
    const drawItems = new L.FeatureGroup();
    map.addLayer(drawItems);

    if (normalizedPolygon.length >= 3) {
      const polygon = L.polygon(normalizedPolygon, {
        ...(mode === "main"
          ? MAIN_POLYGON_PATH_OPTIONS
          : { color: "yellow" }),
      });
      drawItems.addLayer(polygon);
    }

    const drawControl = new L.Control.Draw({
      draw: {
        rectangle: false,
        circle: false,
        marker: false,
        polyline: false,
        circlemarker: false,
        polygon: true,
      },
      edit: {
        featureGroup: drawItems,
        edit: true,
        remove: allowPolygonDelete,
      },
    });

    map.addControl(drawControl);

    let removeButtonCleanup = null;
    if (allowPolygonDelete) {
      const removeButton = drawControl?._container?.querySelector?.(
        ".leaflet-draw-edit-remove",
      );

      if (removeButton) {
        const handleRemoveClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation?.();

          if (drawItems.getLayers().length === 0) {
            return;
          }

          drawItems.clearLayers();

          const { onPolygonDeleted: currentOnPolygonDeleted } =
            callbacksRef.current;
          if (currentOnPolygonDeleted) {
            currentOnPolygonDeleted();
          }
        };

        removeButton.addEventListener("click", handleRemoveClick, true);
        L.DomEvent.disableClickPropagation(removeButton);

        removeButtonCleanup = () => {
          removeButton.removeEventListener("click", handleRemoveClick, true);
        };
      }
    }

    const getPolygonLatLngs = (layer) => {
      if (!(layer instanceof L.Polygon)) {
        return null;
      }

      const latLngs = layer.getLatLngs();

      if (Array.isArray(latLngs?.[0])) {
        return latLngs[0];
      }

      return latLngs;
    };

    const notifyFirstPolygon = (layers, callback) => {
      if (!layers || !callback) {
        return false;
      }

      let hasNotified = false;
      layers.eachLayer((layer) => {
        if (hasNotified) {
          return;
        }

        const latLngs = getPolygonLatLngs(layer);
        if (latLngs && latLngs.length > 0) {
          callback(latLngs);
          hasNotified = true;
        }
      });

      return hasNotified;
    };

    const handleCreated = (e) => {
      const { layerType, layer } = e;
      const { onPolygonCreated: currentOnPolygonCreated } =
        callbacksRef.current;

      if (layerType === "polygon") {
        drawItems.clearLayers();
        drawItems.addLayer(layer);
        if (currentOnPolygonCreated) {
          currentOnPolygonCreated(getPolygonLatLngs(layer));
        }
      }
    };

    const handleEdited = (e) => {
      notifyFirstPolygon(e.layers, callbacksRef.current.onPolygonEdited);
    };

    const handleDraftChanged = (e) => {
      const callback = callbacksRef.current.onPolygonDraftChange;
      const hasNotified = notifyFirstPolygon(e?.layers, callback);

      if (!hasNotified) {
        notifyFirstPolygon(drawItems, callback);
      }
    };

    const handleDeleted = () => {
      const { onPolygonDeleted: currentOnPolygonDeleted } =
        callbacksRef.current;

      if (currentOnPolygonDeleted) {
        currentOnPolygonDeleted();
      }
    };

    const editVertexEvent = L.Draw.Event.EDITVERTEX || "draw:editvertex";
    const editMoveEvent = L.Draw.Event.EDITMOVE || "draw:editmove";
    const editResizeEvent = L.Draw.Event.EDITRESIZE || "draw:editresize";

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(editVertexEvent, handleDraftChanged);
    map.on(editMoveEvent, handleDraftChanged);
    map.on(editResizeEvent, handleDraftChanged);
    map.on("mouseup", handleDraftChanged);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    return () => {
      removeButtonCleanup?.();
      map.removeControl(drawControl);
      map.removeLayer(drawItems);
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(editVertexEvent, handleDraftChanged);
      map.off(editMoveEvent, handleDraftChanged);
      map.off(editResizeEvent, handleDraftChanged);
      map.off("mouseup", handleDraftChanged);
      map.off(L.Draw.Event.DELETED, handleDeleted);
    };
  }, [map, normalizedPolygon, allowPolygonDelete, mode]);

  return null;
};

export const SearchControl = () => {
  const map = useMap();

  useEffect(() => {
    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: true,
    }).addTo(map);

    geocoder.on("markgeocode", function (e) {
      const bbox = e.geocode.bbox;
      const bounds = L.latLngBounds(
        L.latLng(bbox.getSouthWest()),
        L.latLng(bbox.getNorthEast()),
      );
      map.fitBounds(bounds);
    });

    return () => {
      map.removeControl(geocoder);
    };
  }, [map]);

  return null;
};

export const handleKmlUpload = (event, setPolygonCoordinates, setError) => {
  const file = event.target.files[0];

  if (!file) {
    setError?.("Nessun file selezionato");
    return;
  }

  if (
    file.type !== "application/vnd.google-earth.kml+xml" &&
    !file.name.endsWith(".kml")
  ) {
    setError?.("Il file selezionato non è un file KML valido");
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const kmlText = e.target.result;
      const parser = new DOMParser();
      const kml = parser.parseFromString(kmlText, "text/xml");
      const kmlLayer = new L.KML(kml);

      const coordinates = [];
      kmlLayer.eachLayer((layer) => {
        if (layer instanceof L.Polygon) {
          coordinates.push(layer.getLatLngs());
        }
      });

      if (coordinates.length > 0) {
        const latLngArray = coordinates[0][0];
        const convertedCoordinates = latLngArray.map((latLng) => [
          latLng.lat,
          latLng.lng,
        ]);
        setPolygonCoordinates(convertedCoordinates);
        setError?.("");
      } else {
        setError?.("Nessun poligono trovato nel file KML");
      }
    } catch {
      setError?.("Errore durante l'elaborazione del file KML");
    }
  };

  reader.readAsText(file);
};

export const handleEdited = (e, onPolygonEdited) => {
  const layers = e.layers;
  layers.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      const updatedCoordinates = layer.getLatLngs();
      if (onPolygonEdited) {
        onPolygonEdited(updatedCoordinates);
      }
    }
  });
};

export const getPolygonColor = (index) => {
  const colors = [
    "yellow",
    "blue",
    "red",
    "green",
    "purple",
    "orange",
    "pink",
    "cyan",
    "magenta",
    "lime",
  ];
  return colors[index % colors.length];
};

export const isPolygonWithinBounds = (subPolygon, mainPolygon) => {
  const normalizedSubPolygon = normalizePolygonCoordinates(subPolygon);
  const normalizedMainPolygon = normalizePolygonCoordinates(mainPolygon);

  if (
    normalizedSubPolygon.length < 3 ||
    normalizedMainPolygon.length < 3
  ) {
    return false;
  }

  const mainPolygonLatLngs = normalizedMainPolygon.map(({ lat, lng }) =>
    L.latLng(lat, lng),
  );
  const subPolygonLatLngs = normalizedSubPolygon.map(({ lat, lng }) =>
    L.latLng(lat, lng),
  );

  const mainLeafletPolygon = L.polygon(mainPolygonLatLngs);

  return subPolygonLatLngs.every((point) => {
    return (
      mainLeafletPolygon.getBounds().contains(point) &&
      isPointInPolygon(point, mainPolygonLatLngs)
    );
  });
};

export const customIcon = (
  sensorType,
  customIcon,
  customBgColor,
  customIconColor,
  isDisconnected,
) => {
  let Icon, bgColor, iconColor;
  const weatherStation = isWeatherStation(sensorType);

  if (isDisconnected) {
    Icon = weatherStation ? getSensorIcon(sensorType).icon : InfoOutlined;
    bgColor = red[400];
    iconColor = red[50];
  } else if (sensorType === "report") {
    bgColor = grey[300];
    return L.divIcon({
      className: "",
      html: `
      <div style="
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 35px;
          height: 35px;
      ">
        <div style="
          background-color: ${bgColor}; 
          width: 35px; 
          height: 35px; 
          border-radius: 100% 100% 100% 0; 
          transform: rotate(-45deg);
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            color: white; 
            font-size: 16px; 
            transform: rotate(45deg);
            font-weight: bold;
            font-family: Arial, sans-serif;
          ">
            ${renderToString(
              <MenuBook
                style={{
                  color: "black",
                  fontSize: "20px",
                }}
              />,
            )}
          </div>
        </div>
      </div>
    `,
      iconSize: [35, 35],
    });
  } else if (sensorType === "disease") {
    let IconComponent;
    switch (customIcon) {
      case "critical":
        bgColor = "#FF0000";
        IconComponent = Error;
        break;
      case "warning":
        bgColor = "#FFA500";
        IconComponent = Warning;
        break;
      case "healthy":
        bgColor = "#2E7D32";
        IconComponent = CheckCircle;
        break;
      default:
        bgColor = "#FFFF00";
        IconComponent = ErrorOutline;
    }

    return L.divIcon({
      className: "",
      html: `
      <div style="
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 35px;
          height: 35px;
      ">
        <div style="
          background-color: ${bgColor}; 
          width: 35px; 
          height: 35px; 
          border-radius: 100% 100% 100% 0; 
          transform: rotate(-45deg);
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            color: white; 
            font-size: 12px; 
            transform: rotate(45deg);
          ">
            ${renderToString(
              <IconComponent
                style={{
                  color: "white",
                  fontSize: "20px",
                }}
              />,
            )}
          </div>
        </div>
      </div>
    `,
      iconSize: [35, 35],
    });
  } else if (sensorType) {
    const iconData = getSensorIcon(sensorType);
    Icon = iconData.icon;
    bgColor = iconData.bgColor;
    iconColor = iconData.iconColor;
  } else {
    Icon = customIcon || Sensors;
    bgColor = customBgColor || grey[300];
    iconColor = customIconColor || grey[700];
  }

  return L.divIcon({
    className: "",
    html: `
  <div style="
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 35px;
      height: 35px;
  ">
    <div style="
      background-color: ${bgColor}; 
      width: 35px; 
      height: 35px; 
      border-radius: 100% 100% 100% 0; 
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        color: white; 
        font-size: 12px; 
        transform: rotate(45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
      ">
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          transform: ${weatherStation ? "translateY(1px)" : "none"};
        ">
          ${renderToString(<Icon style={{ color: iconColor }} />)}
        </div>
      </div>
      </div>
    </div>
  `,
    iconSize: [30, 30],
  });
};

const doPolygonsIntersect = (polygon1, polygon2) => {
  const normalizedPolygon1 = normalizePolygonCoordinates(polygon1);
  const normalizedPolygon2 = normalizePolygonCoordinates(polygon2);

  if (normalizedPolygon1.length < 3 || normalizedPolygon2.length < 3) {
    return false;
  }

  const poly1LatLngs = normalizedPolygon1.map(({ lat, lng }) =>
    L.latLng(lat, lng),
  );
  const poly2LatLngs = normalizedPolygon2.map(({ lat, lng }) =>
    L.latLng(lat, lng),
  );

  const poly1InPoly2 = poly1LatLngs.some((point) =>
    isPointInPolygon(point, poly2LatLngs),
  );

  const poly2InPoly1 = poly2LatLngs.some((point) =>
    isPointInPolygon(point, poly1LatLngs),
  );

  const edgesIntersect = checkEdgeIntersections(poly1LatLngs, poly2LatLngs);

  return poly1InPoly2 || poly2InPoly1 || edgesIntersect;
};

const checkEdgeIntersections = (poly1, poly2) => {
  for (let i = 0; i < poly1.length; i++) {
    const p1 = poly1[i];
    const p2 = poly1[(i + 1) % poly1.length];

    for (let j = 0; j < poly2.length; j++) {
      const p3 = poly2[j];
      const p4 = poly2[(j + 1) % poly2.length];

      if (doLinesIntersect(p1, p2, p3, p4)) {
        return true;
      }
    }
  }
  return false;
};

const doLinesIntersect = (p1, p2, p3, p4) => {
  const det =
    (p2.lat - p1.lat) * (p4.lng - p3.lng) -
    (p4.lat - p3.lat) * (p2.lng - p1.lng);

  if (det === 0) {
    return false;
  }

  const lambda =
    ((p4.lng - p3.lng) * (p4.lat - p1.lat) +
      (p3.lat - p4.lat) * (p4.lng - p1.lng)) /
    det;
  const gamma =
    ((p1.lng - p2.lng) * (p4.lat - p1.lat) +
      (p2.lat - p1.lat) * (p4.lng - p1.lng)) /
    det;

  return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
};

export const isSubPolygonValid = (
  subPolygon,
  mainPolygon,
  existingSubPolygons = [],
) => {
  if (!isPolygonWithinBounds(subPolygon, mainPolygon)) {
    return {
      isValid: false,
      error:
        "Il sottopoligono deve essere completamente contenuto nel terreno principale",
    };
  }

  for (let i = 0; i < existingSubPolygons.length; i++) {
    const existingSubPolygon = existingSubPolygons[i];

    if (doPolygonsIntersect(subPolygon, existingSubPolygon)) {
      return {
        isValid: false,
        error: `Il sottopoligono interferisce con il sottopoligono ${i + 1}`,
      };
    }
  }

  return {
    isValid: true,
    error: null,
  };
};

export const calculatePolygonCenter = (coordinates) => {
  const normalizedPolygon = normalizePolygonCoordinates(coordinates);

  if (normalizedPolygon.length < 3) {
    return null;
  }

  const polygon = L.polygon(normalizedPolygon);
  const center = polygon.getBounds().getCenter();

  return [center?.lat, center?.lng];
};

export const MarkerDrawControl = ({
  onMarkerCreated,
  markerPosition,
  isReadOnly = false,
}) => {
  const map = useMap();

  useEffect(() => {
    const drawItems = new L.FeatureGroup();
    map.addLayer(drawItems);

    const diseaseMarkerIcon = customIcon("disease", "critical");

    if (markerPosition && markerPosition.length === 2) {
      const marker = L.marker(markerPosition, {
        icon: diseaseMarkerIcon,
      });
      drawItems.addLayer(marker);
    }

    if (!isReadOnly) {
      const drawControl = new L.Control.Draw({
        draw: {
          rectangle: false,
          circle: false,
          marker: {
            icon: diseaseMarkerIcon,
          },
          polyline: false,
          circlemarker: false,
          polygon: false,
        },
        edit: {
          featureGroup: drawItems,
          edit: true,
          remove: true,
        },
      });

      map.addControl(drawControl);

      const handleCreated = (e) => {
        const { layerType, layer } = e;

        if (layerType === "marker") {
          drawItems.clearLayers();

          layer.setIcon(diseaseMarkerIcon);
          drawItems.addLayer(layer);

          const { lat, lng } = layer.getLatLng();

          if (onMarkerCreated) {
            onMarkerCreated([lat, lng]);
          }
        }
      };

      map.on(L.Draw.Event.CREATED, handleCreated);

      return () => {
        map.removeControl(drawControl);
        map.removeLayer(drawItems);
        map.off(L.Draw.Event.CREATED, handleCreated);
      };
    }

    return () => {
      map.removeLayer(drawItems);
    };
  }, [map, onMarkerCreated, markerPosition, isReadOnly]);
  return null;
};
